import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, TextField, Button, Card, CardContent, CircularProgress,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatAmount } from '../../utils/formatAmount';
import ReportService from './ReportService';
import OrdererPaymentSection from './OrdererPaymentSection';
import ExpenseReminderPanel from './ExpenseReminderPanel';

const fmt = (n) => (n == null ? '—' : formatAmount(Math.round(n)));
const fmtRate = (n) => (n == null || n === 0 ? '—' : n.toFixed(4));

const colorCell = (n, invert = false) => {
  if (n == null) return {};
  const positive = invert ? n < 0 : n > 0;
  return { color: positive ? '#2e7d32' : n === 0 ? 'inherit' : '#c62828', fontWeight: 600 };
};

const COL_HEADER = { fontWeight: 700, whiteSpace: 'nowrap', fontSize: 12 };
const COL_CELL = { whiteSpace: 'nowrap', fontSize: 12, py: 0.8 };

export default function DividendDashboard() {
  const [rows, setRows] = useState([]);
  const [latestEmRate, setLatestEmRate] = useState(0);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMonth, setDialogMonth] = useState('');
  const [dialogJpy, setDialogJpy] = useState('');
  const [dialogNote, setDialogNote] = useState('');
  const [dialogSaving, setDialogSaving] = useState(false);

  const [bankBalance, setBankBalance] = useState('');
  const [buffer, setBuffer] = useState('400000');
  const [partners, setPartners] = useState('3');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ReportService.getDividendDashboard();
      setRows(res.data.rows);
      setLatestEmRate(res.data.latestEmRate);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = (row) => {
    setDialogMonth(row.month);
    setDialogJpy(row.japanFloatJpy != null ? String(row.japanFloatJpy) : '');
    setDialogNote(row.japanSnapshot?.note || '');
    setDialogOpen(true);
  };

  const saveFloat = async () => {
    if (!dialogJpy || isNaN(Number(dialogJpy))) return;
    setDialogSaving(true);
    try {
      await ReportService.saveJapanBalanceSnapshot({ month: dialogMonth + '-01', jpyBalance: Number(dialogJpy), note: dialogNote });
      setDialogOpen(false);
      await load();
    } finally {
      setDialogSaving(false);
    }
  };

  const deleteFloat = async (id) => {
    if (!window.confirm('Delete this Japan balance snapshot?')) return;
    await ReportService.deleteJapanBalanceSnapshot(id);
    await load();
  };

  const latestSnapshot = rows.find(r => r.japanFloatJpy != null);
  const japanFloatTHB = latestSnapshot ? Math.round(latestSnapshot.japanFloatJpy * latestEmRate) : 0;
  const bank = Number(bankBalance) || 0;
  const buf = Number(buffer) || 0;
  const pCount = Number(partners) || 3;
  const distributable = bank - buf;
  const perPartner = pCount > 0 ? Math.round(distributable / pCount) : 0;
  const totalCash = bank + japanFloatTHB;
  const distributableWithJapan = totalCash - buf;
  const perPartnerWithJapan = pCount > 0 ? Math.round(distributableWithJapan / pCount) : 0;

  return (
    <div style={{ padding: 16 }}>
      <Typography variant="h5" gutterBottom fontWeight={700}>
        Dividend & Cash Flow Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Data from Oct 2025 onwards · Exchange rate: 1 JPY = {fmtRate(latestEmRate)} THB
      </Typography>

      {loading ? (
        <CircularProgress sx={{ mt: 4 }} />
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#5C6BC0' }}>
                {['Month', 'Cash In (R)', 'Cash In (B)', 'Total Cash In', '→ Japan', '→ Orderers', 'Expenses', 'Net Thai Cash', 'Japan Float JPY', 'Float Δ JPY', 'Float Δ THB', ''].map(h => (
                  <TableCell key={h} sx={{ ...COL_HEADER, color: '#fff' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.month} hover>
                  <TableCell sx={{ ...COL_CELL, fontWeight: 700 }}>{row.month}</TableCell>
                  <TableCell sx={COL_CELL}>{fmt(row.cashReceipts)}</TableCell>
                  <TableCell sx={COL_CELL}>{fmt(row.cashBeam)}</TableCell>
                  <TableCell sx={{ ...COL_CELL, fontWeight: 700, color: '#2e7d32' }}>{fmt(row.totalCashIn)}</TableCell>
                  <TableCell sx={{ ...COL_CELL, color: '#c62828' }}>{fmt(row.thbToJapan)}</TableCell>
                  <TableCell sx={{ ...COL_CELL, color: row.ordererPayments > 0 ? '#c62828' : 'inherit' }}>{row.ordererPayments > 0 ? fmt(row.ordererPayments) : '—'}</TableCell>
                  <TableCell sx={{ ...COL_CELL, color: row.thaiExpenses > 0 ? '#c62828' : 'inherit' }}>{fmt(row.thaiExpenses)}</TableCell>
                  <TableCell sx={{ ...COL_CELL, ...colorCell(row.netThaiCashFlow) }}>{fmt(row.netThaiCashFlow)}</TableCell>
                  <TableCell sx={COL_CELL}>
                    {row.japanFloatJpy != null ? fmt(row.japanFloatJpy) + ' ¥' : <span style={{ color: '#999' }}>not set</span>}
                  </TableCell>
                  <TableCell sx={{ ...COL_CELL, ...colorCell(row.japanFloatChangeJpy, true) }}>
                    {row.japanFloatChangeJpy != null ? (row.japanFloatChangeJpy > 0 ? '+' : '') + fmt(row.japanFloatChangeJpy) + ' ¥' : '—'}
                  </TableCell>
                  <TableCell sx={{ ...COL_CELL, ...colorCell(row.japanFloatChangeTHB, true) }}>
                    {row.japanFloatChangeTHB != null ? (row.japanFloatChangeTHB > 0 ? '+' : '') + fmt(row.japanFloatChangeTHB) : '—'}
                  </TableCell>
                  <TableCell sx={COL_CELL}>
                    <Tooltip title="Edit Japan float">
                      <IconButton size="small" onClick={() => openDialog(row)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {row.japanSnapshot && (
                      <Tooltip title="Delete snapshot">
                        <IconButton size="small" onClick={() => deleteFloat(row.japanSnapshot.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Card sx={{ mt: 4, maxWidth: 480, backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Dividend Calculator</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Japan float (latest: {latestSnapshot?.month || '—'}) = {fmt(latestSnapshot?.japanFloatJpy)} ¥ ≈ {fmt(japanFloatTHB)} THB @ {fmtRate(latestEmRate)}
          </Typography>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <TextField
              label="Thai bank balance (THB)"
              type="number"
              size="small"
              value={bankBalance}
              onChange={e => setBankBalance(e.target.value)}
              placeholder="e.g. 1500000"
            />
            <TextField
              label="Operating buffer (THB)"
              type="number"
              size="small"
              value={buffer}
              onChange={e => setBuffer(e.target.value)}
            />
            <TextField
              label="Number of partners"
              type="number"
              size="small"
              value={partners}
              onChange={e => setPartners(e.target.value)}
            />
          </div>

          {bank > 0 && (
            <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e0e0e0' }}>
              <Typography variant="body2" color="text.secondary">Thai bank only</Typography>
              <Typography variant="h6" fontWeight={700} color={perPartner > 0 ? '#2e7d32' : '#c62828'}>
                {fmt(perPartner)} THB / partner
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({fmt(bank)} − {fmt(buf)}) ÷ {pCount}
              </Typography>

              <Typography variant="body2" color="text.secondary" mt={1.5}>Thai bank + Japan float</Typography>
              <Typography variant="h6" fontWeight={700} color={perPartnerWithJapan > 0 ? '#1565c0' : '#c62828'}>
                {fmt(perPartnerWithJapan)} THB / partner
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({fmt(bank)} + {fmt(japanFloatTHB)} Japan − {fmt(buf)}) ÷ {pCount}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>

      <ExpenseReminderPanel />
      <OrdererPaymentSection onSaved={load} />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Japan Balance — {dialogMonth}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="JPY balance (¥)"
            type="number"
            value={dialogJpy}
            onChange={e => setDialogJpy(e.target.value)}
            fullWidth
            autoFocus
            placeholder="e.g. 12000000"
          />
          {dialogJpy && !isNaN(Number(dialogJpy)) && (
            <Typography variant="caption" color="text.secondary">
              ≈ {fmt(Math.round(Number(dialogJpy) * latestEmRate))} THB @ {fmtRate(latestEmRate)}
            </Typography>
          )}
          <TextField
            label="Note (optional)"
            value={dialogNote}
            onChange={e => setDialogNote(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveFloat} variant="contained" disabled={dialogSaving || !dialogJpy}>
            {dialogSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
